import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ComponentLibrary = ({ onDragStart }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const componentCategories = [
    { id: 'all', name: 'All', icon: 'Grid3x3' },
    { id: 'layout', name: 'Layout', icon: 'LayoutGrid' },
    { id: 'forms', name: 'Forms', icon: 'FileText' },
    { id: 'navigation', name: 'Nav', icon: 'Menu' },
    { id: 'data', name: 'Data', icon: 'Table' },
    { id: 'media', name: 'Media', icon: 'Image' }
  ];

  const components = [
    { id: 'button', name: 'Button', icon: 'Square', category: 'forms', description: 'Interactive button' },
    { id: 'input', name: 'Input', icon: 'Type', category: 'forms', description: 'Text input' },
    { id: 'select', name: 'Dropdown', icon: 'ChevronDown', category: 'forms', description: 'Selection' },
    { id: 'checkbox', name: 'Checkbox', icon: 'CheckSquare', category: 'forms', description: 'Checkbox' },
    { id: 'textarea', name: 'Text Area', icon: 'AlignLeft', category: 'forms', description: 'Multi-line' },
    { id: 'container', name: 'Container', icon: 'Box', category: 'layout', description: 'Container' },
    { id: 'grid', name: 'Grid', icon: 'LayoutGrid', category: 'layout', description: 'Grid layout' },
    { id: 'card', name: 'Card', icon: 'CreditCard', category: 'layout', description: 'Content card' },
    { id: 'navbar', name: 'Nav Bar', icon: 'Menu', category: 'navigation', description: 'Navigation' },
    { id: 'sidebar', name: 'Sidebar', icon: 'PanelLeft', category: 'navigation', description: 'Side nav' },
    { id: 'breadcrumb', name: 'Breadcrumb', icon: 'ChevronRight', category: 'navigation', description: 'Path' },
    { id: 'table', name: 'Table', icon: 'Table', category: 'data', description: 'Data table' },
    { id: 'list', name: 'List', icon: 'List', category: 'data', description: 'List view' },
    { id: 'chart', name: 'Chart', icon: 'BarChart3', category: 'data', description: 'Chart' },
    { id: 'image', name: 'Image', icon: 'Image', category: 'media', description: 'Image' },
    { id: 'video', name: 'Video', icon: 'Video', category: 'media', description: 'Video' },
    { id: 'icon', name: 'Icon', icon: 'Smile', category: 'media', description: 'Icon' }
  ];

  const filteredComponents = components?.filter(component => {
    const matchesSearch = component?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         component?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesCategory = activeCategory === 'all' || component?.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const handleCategoryClick = (e, categoryId) => {
    e?.preventDefault();
    e?.stopPropagation();
    setActiveCategory(categoryId);
  };

  const handleSearchChange = (e) => {
    const value = e?.target?.value || '';
    setSearchQuery(value);
  };

  const handleComponentDragStart = (e, component) => {
    if (e?.dataTransfer) {
      e.dataTransfer.effectAllowed = 'copy';
      e?.dataTransfer?.setData('component', JSON.stringify({
        ...component,
        id: `${component?.id}-${Date.now()}`
      }));
    }
    if (onDragStart) {
      onDragStart(e, component);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="p-3 border-b border-gray-200 flex-shrink-0">
        <h3 className="font-semibold text-sm text-gray-900 mb-2">
          Components
        </h3>
        <div className="relative">
          <Icon 
            name="Search" 
            size={16} 
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="w-full pl-8 pr-3 py-1.5 bg-gray-50 border border-gray-200 rounded-md text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Category Filters */}
      <div className="flex overflow-x-auto border-b border-gray-200 px-2 py-2 gap-1 flex-shrink-0 scrollbar-hide">
        {componentCategories?.map((category) => (
          <button
            key={category?.id}
            type="button"
            onClick={(e) => handleCategoryClick(e, category?.id)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md whitespace-nowrap transition-all text-xs font-medium flex-shrink-0 ${
              activeCategory === category?.id
                ? 'bg-blue-600 text-white shadow-sm' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Icon name={category?.icon} size={13} />
            <span>{category?.name}</span>
          </button>
        ))}
      </div>

      {/* Component List */}
      <div className="flex-1 overflow-y-auto p-2 min-h-0">
        {filteredComponents?.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-8">
            <Icon name="Search" size={40} className="text-gray-300 mb-2" />
            <p className="text-xs text-gray-500">No components found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-2">
            {filteredComponents?.map((component) => (
              <div
                key={component?.id}
                draggable={true}
                onDragStart={(e) => handleComponentDragStart(e, component)}
                className="flex items-start gap-2.5 p-3 bg-gray-50 rounded-lg cursor-grab active:cursor-grabbing hover:bg-gray-100 hover:shadow-sm transition-all border border-gray-200 select-none"
              >
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name={component?.icon} size={16} className="text-blue-600" />
                </div>
                <div className="flex-1 min-w-0 overflow-hidden">
                  <div className="text-sm font-semibold text-gray-900 break-words leading-tight">
                    {component?.name}
                  </div>
                  <div className="text-xs text-gray-500 break-words leading-tight mt-0.5">
                    {component?.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ComponentLibrary;